// LT-293 / LT-294 / LT-295: typed client for the /crm admin REST API.
// Backs the Settings -> Integrations -> CRM page, sync status dashboard,
// and conflict-resolution UI. All calls require a workspace admin JWT;
// 403 on non-admin requests is surfaced as a typed error from the
// caller's try/catch.
import axios, { AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;
if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is required');
}

const client = axios.create({ baseURL: API_BASE_URL, timeout: 30000 });
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

export type CRMProvider = 'hubspot' | 'salesforce' | 'zoho';
export type CRMConnectionStatus = 'connecting' | 'active' | 'error' | 'disconnected';
export type CRMOpportunitySource = 'cold_caller' | 'crm';
export type ZohoRegion = 'us' | 'eu' | 'in' | 'au' | 'jp' | 'ca';

export interface CRMConnection {
  id: string;
  workspace_id: string;
  provider: CRMProvider;
  provider_region: string | null;
  status: CRMConnectionStatus;
  oauth_token_id: string | null;
  opportunity_source_of_truth: CRMOpportunitySource;
  connected_at: string | null;
  connected_by_user_id: string | null;
  last_error: string | null;
  last_error_at: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface GetConnectionResponse {
  connection: CRMConnection | null;
}

export interface AuthorizeUrlResponse {
  authorize_url: string;
}

export interface SyncNowResponse {
  seen: number;
  updated: number;
  created: number;
  errors: number;
}

export type DashboardEntityType = 'contact' | 'account' | 'opportunity' | 'activity';

export interface SyncStatusError {
  entity_type: DashboardEntityType | string;
  direction: 'push' | 'pull';
  sync_status: string;
  error: string;
  when: string | null;
}

export interface SyncStatusResponse {
  connection: CRMConnection;
  counts: Record<DashboardEntityType, number>;
  last_pushed_at: Record<DashboardEntityType, string | null>;
  last_pulled_at: Record<DashboardEntityType, string | null>;
  recent_errors: SyncStatusError[];
}

export interface CRMConflictDetail {
  id: string;
  workspace_id: string;
  crm_connection_id: string;
  local_entity_type: string;
  local_entity_id: string;
  remote_entity_id: string;
  remote_entity_type: string;
  sync_status: string;
  last_pushed_at: string | null;
  last_pulled_at: string | null;
  push_error: string | null;
  pull_error: string | null;
  local_data: Record<string, unknown> | null;
  remote_data: Record<string, unknown> | null;
  conflicting_fields: string[];
  created_at: string;
  updated_at: string | null;
}

// LT-397 — On-demand CRM contact listing + import into a static
// Contact List, used by the composer's "From CRM" mode.

export interface CRMRemoteContactSummary {
  external_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  source: 'hubspot' | 'salesforce' | 'zoho' | string;
}

export interface CRMContactsListResponse {
  contacts: CRMRemoteContactSummary[];
  next_cursor: string | null;
}

export type CRMImportDedupe = 'by_email' | 'by_phone' | 'none';

export interface CRMImportContactsResponse {
  imported: number;
  skipped_dupe: number;
  errors: Array<{ external_id: string; message: string }>;
}

export const crmSyncAPI = {
  getConnection: async (): Promise<CRMConnection | null> => {
    const r = await client.get<GetConnectionResponse>('/crm/connection');
    return r.data.connection;
  },
  updateConnectionSettings: async (
    patch: { opportunity_source_of_truth: CRMOpportunitySource },
  ): Promise<CRMConnection> => {
    const r = await client.patch<CRMConnection>('/crm/connection', patch);
    return r.data;
  },
  disconnect: async (): Promise<void> => {
    await client.delete('/crm/connection');
  },
  startHubspotOAuth: async (): Promise<AuthorizeUrlResponse> => {
    const r = await client.post<AuthorizeUrlResponse>('/crm/hubspot/connect');
    return r.data;
  },
  startSalesforceOAuth: async (
    params: { my_domain_url: string },
  ): Promise<AuthorizeUrlResponse> => {
    const r = await client.post<AuthorizeUrlResponse>('/crm/salesforce/connect', params);
    return r.data;
  },
  startZohoOAuth: async (
    params: { region: ZohoRegion },
  ): Promise<AuthorizeUrlResponse> => {
    const r = await client.post<AuthorizeUrlResponse>('/crm/zoho/connect', params);
    return r.data;
  },
  syncNow: async (): Promise<SyncNowResponse> => {
    const r = await client.post<SyncNowResponse>('/crm/sync-now');
    return r.data;
  },
  getSyncStatus: async (): Promise<SyncStatusResponse> => {
    const r = await client.get<SyncStatusResponse>('/crm/sync-status');
    return r.data;
  },
  listConflicts: async (): Promise<CRMConflictDetail[]> => {
    const r = await client.get<CRMConflictDetail[]>('/crm/conflicts');
    return r.data;
  },
  resolveConflict: async (
    mappingId: string,
    side: 'local' | 'remote',
  ): Promise<CRMConflictDetail> => {
    const r = await client.post<CRMConflictDetail>(
      `/crm/conflicts/${mappingId}/resolve`, { side },
    );
    return r.data;
  },
  // LT-397 — list contacts in the workspace's connected CRM, used by
  // the contact-list composer's "From CRM" picker. Pagination is opaque
  // (cursor passed through as-is).
  listContacts: async (params: {
    q?: string;
    limit?: number;
    cursor?: string | null;
  }): Promise<CRMContactsListResponse> => {
    const query: Record<string, string> = {};
    if (params.q && params.q.trim()) query.q = params.q.trim();
    if (params.limit) query.limit = String(params.limit);
    if (params.cursor) query.cursor = params.cursor;
    const r = await client.get<CRMContactsListResponse>(
      '/crm/contacts', { params: query },
    );
    return r.data;
  },
  // LT-397 — import the picker's selection into a static list. Returns
  // the imported / skipped / per-row-error envelope so the UI can show
  // a result panel.
  importToList: async (
    listId: string,
    body: { external_ids: string[]; dedupe?: CRMImportDedupe },
  ): Promise<CRMImportContactsResponse> => {
    const r = await client.post<CRMImportContactsResponse>(
      `/contact-lists/${listId}/import-from-crm`, body,
    );
    return r.data;
  },
};

/**
 * Interpret an AxiosError from the /crm API as either a 403 (not a
 * workspace admin -> read-only UX) or a message-bearing error. Callers
 * switch on the return shape to render the right message.
 */
export function asCrmApiError(err: unknown): {
  code?: string;
  message: string;
  isForbidden: boolean;
} {
  const ax = err as AxiosError<{ detail?: { code?: string; message?: string } | string }>;
  const status = ax?.response?.status;
  if (status === 403) {
    return { isForbidden: true, message: 'Workspace admin access required.' };
  }
  const detail = ax?.response?.data?.detail;
  if (typeof detail === 'object' && detail !== null) {
    return {
      isForbidden: false,
      code: detail.code,
      message: detail.message || 'Request failed.',
    };
  }
  if (typeof detail === 'string') {
    return { isForbidden: false, message: detail };
  }
  return { isForbidden: false, message: ax?.message || 'Request failed.' };
}
